import {createFocusTrap, FocusTrap} from 'focus-trap';
import {
    VanillaBookmarkReadingPositionIntentEventCallback, VanillaMenuHiddenCallback,
    VanillaVoidCallback
} from '../VanillaReader/VanillaReaderModel';
import {VanillaReaderUI} from './VanillaReaderUI';
import {BrowserDetector} from "@colibrio/colibrio-reader-framework/colibrio-core-base";
import {VanillaReaderUiSliderProgression} from "./VanillaReaderUiSliderProgression";

/**
 * # VanillaReaderUiMenu
 *
 * ## RESPONSIBILITIES
 *
 * This class hosts a number of child UI components that make up the bottom menu.
 *
 * - Progression Slider
 * - Media Player
 * - Action Buttons
 *
 * ## RELATED TYPES
 * - VanillaReaderUiMenuController
 *
 */

export class VanillaReaderUiMenu {
    private _menu: HTMLElement;
    private _buttonMenuHandle: HTMLElement;
    private _buttonToc: HTMLElement;
    private _buttonBookmarks: HTMLElement;
    private _buttonHighlights: HTMLElement;
    private _buttonHelp: HTMLElement;
    //private _buttonFile: HTMLElement;
    private _buttonSettings: HTMLElement;
    private _buttonBookmarkReadingPosition: HTMLElement;
    private _buttonReadAloudOpen: HTMLElement;
    private _buttonReadAloudClose: HTMLElement;
    private _buttonPanZoomActivate: HTMLElement;
    private _buttonPanZoomDeactivate: HTMLElement;
    //private _buttonRulerActivate: HTMLElement;
    //private _buttonRulerDeactivate: HTMLElement;
    private _buttonSearch: HTMLElement;
    private _buttonNavigationHistory: HTMLElement;
    private _hostDocument: Document;
    private _enablePanZoomButton: boolean = !BrowserDetector.isPlatform('mobile');
    private _sliderProgression: VanillaReaderUiSliderProgression | undefined;

    private _touchEventCoords: number[] | undefined;

    private _focusTrap: FocusTrap | undefined;

    private _menuHandleClickedCallback: VanillaVoidCallback | undefined;
    private _openHelpDialogCallback: VanillaVoidCallback | undefined;
    private _swipeDownCallback: VanillaVoidCallback | undefined;
    //private _fileOpenCallback: VanillaVoidCallback | undefined;
    private _visibleCallback: VanillaVoidCallback | undefined;
    private _hiddenCallback: VanillaMenuHiddenCallback | undefined;
    private _showReadAloudControlsCallback: VanillaVoidCallback | undefined;
    private _hideReadAloudControlsCallback: VanillaVoidCallback | undefined;
    private _panZoomActivateCallback: VanillaVoidCallback | undefined;
    private _panZoomDeactivateCallback: VanillaVoidCallback | undefined;
    private _searchPublicationCallback: VanillaVoidCallback | undefined;
    private _bookmarkReadingPositionIntentCallback: VanillaBookmarkReadingPositionIntentEventCallback | undefined;
    private _contentsDialogOpenCallback: VanillaVoidCallback | undefined = undefined;
    //private _readingRulerActivateCallback: VanillaVoidCallback | undefined = undefined;
    //private _readingRulerDeactivateCallback: VanillaVoidCallback | undefined = undefined;
    private _showNavigationHistoryCallback: VanillaVoidCallback | undefined = undefined;

    private _style = `
        <style>
            #vanilla-reader__menu {                
                position: fixed;
                bottom: 0;
                opacity: 0;
                display: flex;  
                width: 100%;                
                margin-left: auto;
                margin-right: auto;
                padding: 1em;
                flex-direction: row;
                flex-wrap: wrap;
                justify-content: space-around;
                box-sizing: border-box;
                z-index: 2;
                background-color: var(--vanilla-reader__color__ui__background-light);                
                box-shadow: 0 -3px 16px 0 rgb(0 0 0 / 30%);
                border-radius: 16px 16px 0 0;
                border: none;
                border-top: 1px solid var(--vanilla-reader__color__ui__outline-dark);
                pointer-events: none;                
                transition: bottom 0.2s ease-out;
                will-change: bottom;
            }
                    
            @media (prefers-reduced-motion: reduce) {
                #vanilla-reader__menu {
                    transition: opacity 0.2s ease-out;
                }
            }
            
            #vanilla-reader__menu button {
                flex-direction: column;                
                min-height: 48px;
                min-width: 48px;
                font-size: x-large;
                color: var(--vanilla-reader__color__ui__foreground-dark);
                border-color: transparent;
                background-color: transparent;                
            }           
            
            #vanilla-reader__menu button .vanilla__button-icon { 
                display: block;
                width: 32px;   
                height: 32px;   
                margin-bottom: 5px;
            }
            
            #vanilla-reader__menu button .vanilla__button-label { 
                font-family: 'Lato';
                font-size: 10px;
                font-weight: 600;
                min-height: 20px;                
                display: block;            
            }
                        
            #vanilla-reader__menu__button-handle {
                height: 24px;                
                min-height: 24px!important;
                width: 100%;
                margin: 8px;
                align-self: center;                
                background-color: transparent;               
                border: 0;
            }

            #vanilla-reader__menu__button-handle span {
                width: 100%;
                display: block;
                font-size: x-large;
                color: var(--vanilla-reader__color__ui__foreground-dark);
            }
                        
            #vanilla-reader__menu__mediaplayer-container {
                width: 100%;
                justify-content: center;
                font-size: x-large;
            }
                        
            #vanilla-reader__menu__slider-container {
                display: flex;
                flex-grow: 1;
                flex-direction: row;
                flex-wrap: wrap;            
                width: 100%; 
                max-width: 900px;            
            }

            #vanilla-reader__menu__action-button-container {
                display: flex;
                flex-grow: 1;
                width: 100%;       
                max-width: max-content;          
                align-items: center;
                flex-direction: row;                
                justify-content: space-between;
                overflow-x: auto;
            }                    
            
            #vanilla-reader__menu__action-button-container > button {
                min-width: 64px;
            }
            
            #vanilla-reader__dialog-highlight__form__comment {
                width: 100%;
                height: 10em;
                margin-bottom: 1em;
            }
            
            #vanilla-reader__menu__button-bookmark-add {}
            
            #vanilla-reader__menu__button-bookmark-remove {
                display: none;
            }
            
            #vanilla-reader__menu__button-readaloud-hide {
                display: none;
            }
            
            @media only screen and (max-device-width: 1000px) {
                #vanilla-reader__menu__action-button-container {
                    justify-content: space-between;
                }
            }
            
        </style>`;

    private _template = `
        <nav id="vanilla-reader__menu" title="Menu section" aria-hidden="true" inert>      
            <button id="vanilla-reader__menu__button-handle" title="Close menu" accesskey="x"><span aria-hidden="true">drag_handle</span></button>
            <div id="vanilla-reader__menu__mediaplayer-container" inert></div>
            <div id="vanilla-reader__menu__slider-container" inert></div>
            <hr>
            <div id="vanilla-reader__menu__action-button-container">
                <button id="vanilla-reader__menu__button-help" title="Open help dialog"><span class="vanilla__button-icon" aria-hidden="true">help</span><span aria-hidden="true" class="vanilla__button-label">Help</span></button>
                <!--<button id="vanilla-reader__menu__button-file" title="Open new book" accesskey="q"><span class="vanilla__button-icon" aria-hidden="true">folder_open</span><span aria-hidden="true" class="vanilla__button-label">Open file</span></button>-->
                <button id="vanilla-reader__menu__button-toc" title="Open table of contents dialog" accesskey="c"><span class="vanilla__button-icon" aria-hidden="true">toc</span><span aria-hidden="true" class="vanilla__button-label">Contents</span></button>
                <button id="vanilla-reader__menu__button-navigation-history" title="Open navigation history" accesskey="y"><span class="vanilla__button-icon" aria-hidden="true">history</span><span aria-hidden="true" class="vanilla__button-label">History</span></button>
                <button id="vanilla-reader__menu__button-bookmarks" title="Open bookmarks dialog" accesskey="o"><span class="vanilla__button-icon" aria-hidden="true">bookmarks</span><span aria-hidden="true" class="vanilla__button-label">Bookmarks</span></button>
                <button id="vanilla-reader__menu__button-highlights" title="Open highlights dialog" accesskey="h"><span class="vanilla__button-icon" aria-hidden="true">edit_note</span><span aria-hidden="true" class="vanilla__button-label">Highlights</span></button>
                <button id="vanilla-reader__menu__button-bookmark-readingposition" title="Bookmark current position" accesskey="b"><span class="vanilla__button-icon" aria-hidden="true">bookmark_add</span><span aria-hidden="true" class="vanilla__button-label">Add Bookmark</span></button>
                <button id="vanilla-reader__menu__button-readaloud-show" title="Show read aloud controls" accesskey="r"><span class="vanilla__button-icon" aria-hidden="true">hearing</span><span aria-hidden="true" class="vanilla__button-label">Read Aloud</span></button>
                <button id="vanilla-reader__menu__button-readaloud-hide" title="Show read aloud controls"><span class="vanilla__button-icon" aria-hidden="true">hearing_disabled</span><span aria-hidden="true" class="vanilla__button-label">Read Aloud</span></button>
                <button id="vanilla-reader__menu__button-panzoom-activate" title="Activate Pan and Zoom mode"><span class="vanilla__button-icon" aria-hidden="true">zoom_in</span><span aria-hidden="true" class="vanilla__button-label">Zoom</span></button>
                <button id="vanilla-reader__menu__button-panzoom-deactivate" title="Exit Pan and Zoom mode"><span class="vanilla__button-icon" aria-hidden="true">zoom_out_map</span><span aria-hidden="true" class="vanilla__button-label">Exit Zoom</span></button>
                <!--<button id="vanilla-reader__menu__button-ruler-activate" title="Activate Reading Ruler"><span class="vanilla__button-icon" aria-hidden="true">straighten</span><span aria-hidden="true" class="vanilla__button-label">Ruler (Beta)</span></button>
                <button id="vanilla-reader__menu__button-ruler-deactivate" title="Deactivate Reading Ruler"><span class="vanilla__button-icon" aria-hidden="true">straighten</span><span aria-hidden="true" class="vanilla__button-label">Hide ruler</span></button>-->                
                <button id="vanilla-reader__menu__button-search" title="Search book" accesskey="s"><span class="vanilla__button-icon" aria-hidden="true">search</span><span aria-hidden="true" class="vanilla__button-label">Search</span></button>
                <button id="vanilla-reader__menu__button-settings" title="Open settings dialog" accesskey="t"><span class="vanilla__button-icon" aria-hidden="true">settings</span><span aria-hidden="true" class="vanilla__button-label">Settings</span></button>
            </div>
        </nav>`;

    constructor(
        private _hostElement: HTMLElement,
        insertPosition: InsertPosition = 'beforeend',
        private _hostContext: VanillaReaderUI,
    ) {

        this._hostElement.insertAdjacentHTML(insertPosition, this._template);
        this._hostDocument = _hostElement.ownerDocument!;

        this._menu = this._hostDocument.getElementById('vanilla-reader__menu')!;
        this._menu.insertAdjacentHTML('afterbegin', this._style);
        this._buttonMenuHandle = this._hostDocument.getElementById('vanilla-reader__menu__button-handle')!;
        this._buttonHelp = this._hostDocument.getElementById('vanilla-reader__menu__button-help')!;
        //this._buttonFile = this._hostDocument.getElementById('vanilla-reader__menu__button-file')!;
        this._buttonToc = this._hostDocument.getElementById('vanilla-reader__menu__button-toc')!;
        this._buttonBookmarks = this._hostDocument.getElementById('vanilla-reader__menu__button-bookmarks')!;
        this._buttonHighlights = this._hostDocument.getElementById('vanilla-reader__menu__button-highlights')!;
        this._buttonSettings = this._hostDocument.getElementById('vanilla-reader__menu__button-settings')!;
        this._buttonBookmarkReadingPosition = this._hostDocument.getElementById('vanilla-reader__menu__button-bookmark-readingposition')!;
        this._buttonReadAloudOpen = this._hostDocument.getElementById('vanilla-reader__menu__button-readaloud-show')!;
        this._buttonReadAloudClose = this._hostDocument.getElementById('vanilla-reader__menu__button-readaloud-hide')!;
        this._buttonSearch = this._hostDocument.getElementById('vanilla-reader__menu__button-search')!;
        this._buttonPanZoomActivate = this._hostDocument.getElementById('vanilla-reader__menu__button-panzoom-activate')!;
        this._buttonPanZoomDeactivate = this._hostDocument.getElementById('vanilla-reader__menu__button-panzoom-deactivate')!;
        //this._buttonRulerActivate = this._hostDocument.getElementById('vanilla-reader__menu__button-ruler-activate')!;
        //this._buttonRulerDeactivate = this._hostDocument.getElementById('vanilla-reader__menu__button-ruler-deactivate')!;
        this._buttonNavigationHistory = this._hostDocument.getElementById('vanilla-reader__menu__button-navigation-history')!;

        this._menu.addEventListener('keyup', this._event_menu_keyup);

        this._menu.inert = true;

        // Ok, we have references to all the menu component's HTML elements. Now let's setup various behaviours such as
        // event listeners.
        this._setup();

    }

    onMenuHandleSwipeDown(callback: VanillaVoidCallback): void {
        this._swipeDownCallback = callback;
    }

    onHelpOpen(callback: VanillaVoidCallback): void {
        this._openHelpDialogCallback = callback;
    }

    onMenuHandleClicked(callback: VanillaVoidCallback): void {
        this._menuHandleClickedCallback = callback;
    }

    /*onFileOpen(callback: VanillaVoidCallback): void {
        this._fileOpenCallback = callback;
    }*/

    onContentsOpen(callback: VanillaVoidCallback): void {
        this._contentsDialogOpenCallback = callback;
    }

    onMenuVisible(callback: VanillaVoidCallback): void {
        this._visibleCallback = callback;
    }

    onMenuHidden(callback: VanillaMenuHiddenCallback): void {
        this._hiddenCallback = callback;
    }

    onBookmarkReadingPosition(callback: VanillaBookmarkReadingPositionIntentEventCallback): void {
        this._bookmarkReadingPositionIntentCallback = callback;
    }

    onReadAloudControlsOpen(callback: VanillaVoidCallback): void {
        this._showReadAloudControlsCallback = callback;
    }

    onReadAloudControlsClose(callback: VanillaVoidCallback): void {
        this._hideReadAloudControlsCallback = callback;
    }

    onPanZoomActivate(callback: VanillaVoidCallback): void {
        this._panZoomActivateCallback = callback;
    }

    onPanZoomDeactivate(callback: VanillaVoidCallback): void {
        this._panZoomDeactivateCallback = callback;
    }

    onPublicationSearch(callback: VanillaVoidCallback): void {
        this._searchPublicationCallback = callback;
    }

    /*onReadingRulerShow(callback: VanillaVoidCallback): void {
        this._readingRulerActivateCallback = callback;
    }

    onReadingRulerHide(callback: VanillaVoidCallback): void {
        this._readingRulerDeactivateCallback = callback;
    }*/

    onNavigationHistoryShow(callback: VanillaVoidCallback): void {
        this._showNavigationHistoryCallback = callback;
    }

    getComponentElement(): HTMLElement {
        return this._hostDocument.getElementById('vanilla-reader__menu')!;
    }

    getMediaPlayerContainerElement(): HTMLElement {
        return this._hostDocument.getElementById('vanilla-reader__menu__mediaplayer-container')!;
    }

    getProgressionSliderContainerElement(): HTMLElement {
        return this._hostDocument.getElementById('vanilla-reader__menu__slider-container')!;
    }

    setAsImportantForAccessibility(): void {
        this._menu.removeAttribute('aria-hidden');
    }

    setAsNotImportantForAccessibility(): void {
        this._menu.setAttribute('aria-hidden', 'true');
    }

    public get isVisible(): boolean {
        return this._menu.style.opacity === '1';
    }

    show(animate: boolean = true): void {
        if (!animate) {
            this._menu.style.transition = 'opacity 0.2s ease-out';
        } else {
            this._menu.style.transition = 'bottom 0.2s ease-out';
        }

        this._toggleMenuButtonsKeyboardFocusable(true);
        this.getProgressionSliderContainerElement().tabIndex = 0;
        this.getProgressionSliderContainerElement().inert = false;
        this.getMediaPlayerContainerElement().inert = false;

        this._menu.tabIndex = 0;
        this._menu.inert = false;
        this._menu.style.opacity = '1';
        this._menu.style.bottom = '0';

        this._focusTrap = createFocusTrap(this._menu, {
            allowOutsideClick: true,
            escapeDeactivates: false,
            returnFocusOnDeactivate: true,
        });
        this._focusTrap.activate();

        // Wait until just a bit before turning on events and calling the callback function.
        window.setTimeout(() => {
            this._menu.style.pointerEvents = 'auto';

            if (this._visibleCallback) {
                this._visibleCallback();
            }

        }, 500);

    }

    hide(animate: boolean = true, silent: boolean = false): void {
        if (!animate) {
            this._menu.style.transition = 'opacity 0.2s ease-out';
        } else {
            this._menu.style.transition = 'bottom 0.2s ease-out';
        }

        this._menu.tabIndex = -1;
        this.getProgressionSliderContainerElement().tabIndex = -1;
        this.getProgressionSliderContainerElement().inert = true;
        this.getMediaPlayerContainerElement().inert = true;

        this._toggleMenuButtonsKeyboardFocusable(false);
        this._menu.inert = true;

        this._menu.style.pointerEvents = 'none';
        this._menu.style.bottom = `${-(this._menu.clientHeight + 16)}px`;

        // Wait for the animation to finish before calling callback method etc.
        window.setTimeout(() => {
            this._menu.style.opacity = '0';

            if (this._focusTrap) {

                let options: { returnFocus: boolean } = {
                    returnFocus: false,
                };
                try {
                    this._focusTrap.deactivate(options);
                } catch (e) {

                }
            }

            if (this._hiddenCallback) {
                this._hiddenCallback( 'menu', silent);
            }
        }, 400);
    }

    toggleReadAloudButtonState(on: boolean): void {
        if (on) {
            this._buttonReadAloudClose.style.display = 'flex';
            this._buttonReadAloudOpen.style.display = 'none';
        } else {
            this._buttonReadAloudOpen.style.display = 'flex';
            this._buttonReadAloudClose.style.display = 'none';
        }

    }

    togglePanZoomButtonState(on: boolean): void {
        if (on) {
            this._buttonPanZoomDeactivate.style.display = 'flex';
            this._buttonPanZoomActivate.style.display = 'none';
        } else {
            this._buttonPanZoomActivate.style.display = 'flex';
            this._buttonPanZoomDeactivate.style.display = 'none';
        }

    }

    /*toggleRulerButtonState(on: boolean): void {
        if (on) {
            this._buttonRulerDeactivate.style.display = 'flex';
            this._buttonRulerActivate.style.display = 'none';
        } else {
            this._buttonRulerActivate.style.display = 'flex';
            this._buttonRulerDeactivate.style.display = 'none';
        }
    }*/

    /*
    *
    * PRIVATE METHODS
    *
    * */

    private _setup(): void {

        this.hide();

        this._buttonMenuHandle.addEventListener('click', (_ev) => {
            if (this._menuHandleClickedCallback) {
                this._menuHandleClickedCallback();
            }
        });

        this._buttonMenuHandle.addEventListener('touchstart', (ev) => {
            this._touchEventCoords = [ev.changedTouches[0].clientX, ev.changedTouches[0].clientY];
        });

        this._buttonMenuHandle.addEventListener('touchend', (ev) => {
            if (this._touchEventCoords && this._touchEventCoords[1] - ev.changedTouches[0].clientY <= -96) {
                if (this._swipeDownCallback) {
                    this._swipeDownCallback();
                }
            }
        });

        /*this._buttonFile.addEventListener('click', (_ev) => {
            if (this._fileOpenCallback) {
                this._fileOpenCallback();
            }
        });*/

        this._buttonBookmarks.addEventListener('click', (_ev) => {
            if (!this._hostContext.dialogBookmarks?.isOpen()) {
                this._hostContext.dialogBookmarks?.show();
            } else {
                this._hostContext.dialogBookmarks?.close();
            }
        });

        this._buttonHighlights.addEventListener('click', (_ev) => {
            if (!this._hostContext.dialogHighlights?.isOpen()) {
                this._hostContext.dialogHighlights?.show();
            } else {
                this._hostContext.dialogHighlights?.close();
            }
        });

        this._buttonSettings.addEventListener('click', (_ev) => {
            if (this._hostContext.dialogSettings?.isOpen()) {
                this._hostContext.dialogSettings.close();
            } else {
                this._hostContext.dialogSettings?.show();
            }
        });

        this._buttonBookmarkReadingPosition.addEventListener('click', (_ev) => {
            if (this._bookmarkReadingPositionIntentCallback) {
                this._bookmarkReadingPositionIntentCallback();
            }
        });

        this._buttonReadAloudOpen.addEventListener('click', (_ev) => {
            // Toggle the show/hide button-pair state
            this._buttonReadAloudOpen.style.display = 'none';
            this._buttonReadAloudClose.style.display = 'flex';

            if (this._showReadAloudControlsCallback) {
                this._showReadAloudControlsCallback();
            }
        });

        this._buttonReadAloudClose.addEventListener('click', (_ev) => {
            // Toggle the show/hide button-pair state
            this._buttonReadAloudClose.style.display = 'none';
            this._buttonReadAloudOpen.style.display = 'flex';

            if (this._hideReadAloudControlsCallback) {
                this._hideReadAloudControlsCallback();
            }
        });

        this._buttonSearch.addEventListener('click', (_ev) => {
            if (this._searchPublicationCallback) {
                this._searchPublicationCallback();
            }
        });

        this._buttonHelp.addEventListener('click', (_ev) => {
            if (this._openHelpDialogCallback) {
                this._openHelpDialogCallback();
            }
        });

        this._buttonToc.addEventListener('click', (ev) => {
            // This event must be prevented from propagation to not fire a click
            // on the selected navigation item in the contents dialog, if that
            // item has received focus before the callback has run.

            ev.stopImmediatePropagation();
            ev.preventDefault();
            ev.cancelBubble = true;
            if (this._contentsDialogOpenCallback) {
                this._contentsDialogOpenCallback();
            }
        });

        if(this._sliderProgression) {
            this._sliderProgression.disabled = true;
        }

        if(this._enablePanZoomButton) {
            this._buttonPanZoomActivate.addEventListener('click', (_ev) => {
                this._buttonPanZoomActivate.style.display = 'none';
                this._buttonPanZoomDeactivate.style.display = 'flex';

                if (this._panZoomActivateCallback) {
                    this._panZoomActivateCallback();
                }
            });

            this._buttonPanZoomDeactivate.addEventListener('click', (_ev) => {
                this._buttonPanZoomDeactivate.style.display = 'none';
                this._buttonPanZoomActivate.style.display = 'flex';

                if (this._panZoomDeactivateCallback) {
                    this._panZoomDeactivateCallback();
                }
            });
        } else {
            this._buttonPanZoomActivate.style.display = 'none';
        }

        this._buttonPanZoomDeactivate.style.display = 'none';

        /*this._buttonRulerActivate.addEventListener('click', (_ev) => {
            this._buttonRulerActivate.style.display = 'none';
            this._buttonRulerDeactivate.style.display = 'flex';

            if (this._readingRulerActivateCallback) {
                this._readingRulerActivateCallback();
            }
        });

        this._buttonRulerDeactivate.addEventListener('click', (_ev) => {
            this._buttonRulerDeactivate.style.display = 'none';
            this._buttonRulerActivate.style.display = 'flex';

            if (this._readingRulerDeactivateCallback) {
                this._readingRulerDeactivateCallback();
            }
        });

        this._buttonRulerDeactivate.style.display = 'none';*/

        this._buttonNavigationHistory.addEventListener('click', (_ev) => {
            if(this._showNavigationHistoryCallback) {
                this._showNavigationHistoryCallback();
            }
        })

    }

    private _toggleMenuButtonsKeyboardFocusable(focusable: boolean): void {
        let tabIndex = 0;
        if(!focusable) {
            tabIndex = -1;
        }
        this._menu.querySelectorAll('button').forEach(button => button.tabIndex = tabIndex);
    }

    /*
    *
    * EVENT HANDLERS
    *
    * */

    private _event_menu_keyup = (ev: KeyboardEvent) => {
        switch (ev.key) {
            case 'Escape':
                this.hide();
                break;
        }
    };
}
